import { ActivatedRouteSnapshot, Route } from '@angular/router';
import { AuthGuard } from '@auth0/auth0-angular';
import { MainLayoutComponent } from './shared/components/main-layout/main-layout.component';
import {
  AccessDeniedRiComponent,
  AuthorizationRiGuard,
  DirtyCheckGuard,
} from '@r1-development/rps-shared-ui';
import { CallbackComponent } from './routes/callback/callback.component';
import { FilesDownloadComponent } from './routes/files-download/files-download.component';
import { RoutesWithSidebarComponent } from './routes/routes-with-sidebar/routes-with-sidebar.component';
import { EntityIds } from './shared/models';
import { inject } from '@angular/core';
import { Permissions } from './shared/models/permissions.model';
import { AccessRequestComponent } from './routes/access-request/access-request.component';
import { SidebarState } from './routes/routes-with-sidebar/shared/components/sidebar/state/sidebar.state';
import { collapsedSetter } from './routes/routes-with-sidebar/shared/components/sidebar/state/state-updaters/sidebar-updaters';
import { userLoadedGuard } from './shared/guards/user-loaded.guard';

export const AFTER_TASK_COMPLETION_SEGMENTS = ['task-groups'];

export const appRoutes: Route[] = [
  // Public routes that must remain accessible without user-loaded guard
  {
    path: 'logged-out',
    title: 'Logged Out',
    loadComponent: () =>
      import('./routes/logged-out/logged-out.component').then(
        (m) => m.LoggedOutComponent
      ),
  },
  {
    path: 'callback',
    component: CallbackComponent,
  },
  // Secured routes: apply userLoadedGuard implicitly to all children via canActivate
  {
    path: '',
    canActivate: [AuthGuard, userLoadedGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: AFTER_TASK_COMPLETION_SEGMENTS.join('/'),
      },
      {
        path: 'access-denied',
        component: AccessDeniedRiComponent,
        title: 'Access Denied',
      },
      {
        path: 'template-library',
        // No runGuardsAndResolvers override: the page syncs its active tab to a
        // `tab` query param, and re-running DirtyCheckGuard on a query-param-only
        // change would prompt to discard changes on every tab switch.
        canDeactivate: [DirtyCheckGuard],
        loadComponent: () =>
          import('./routes/template-library/template-library.component').then(
            (c) => c.TemplateLibraryComponent
          ),
      },
      {
        path: 'my-profile',
        loadComponent: () =>
          import('./routes/my-profile/my-profile.component').then(
            (c) => c.MyProfileComponent
          ),
      },
      {
        path: 'users',
        pathMatch: 'full',
        loadComponent: () =>
          import('./routes/users/users.component').then(
            (c) => c.UsersComponent
          ),
      },
      {
        path: 'users/:userid',
        loadComponent: () =>
          import('./routes/user-profile/user-profile.component').then(
            (c) => c.UserProfileComponent
          ),
        canActivate: [AuthorizationRiGuard],
        data: {
          permissions: [Permissions.ResolutionUsersEdit],
        },
        resolve: {
          userId: (route: ActivatedRouteSnapshot) => {
            const userId = route.params['userid'] || '';
            return userId;
          },
        },
      },
      {
        path: 'files/:fileid',
        component: FilesDownloadComponent,
        title: 'Files',
        resolve: {
          resolverFileId: (route: ActivatedRouteSnapshot) => {
            return route.params['fileid'];
          },
        },
      },
      {
        path: 'preview/:fileid',
        loadComponent: () =>
          import('./routes/file-preview/file-preview.component').then(
            (c) => c.FilePreviewComponent
          ),
        title: 'Preview',
        resolve: {
          resolverFileId: (route: ActivatedRouteSnapshot) => {
            return route.params['fileid'];
          },
        },
      },
      {
        path: 'toolbox',
        title: 'Toolbox',
        loadChildren: () =>
          import('./routes/toolbox/toolbox.routes').then(
            (r) => r.toolboxRoutes
          ),
      },
      {
        path: 'payer-config',
        title: 'Payer Configuration',
        canActivate: [AuthorizationRiGuard],
        data: {
          permissions: [Permissions.ResolutionAccess],
        },
        loadChildren: () =>
          import('./routes/payer-config/payer-config.routes').then(
            (m) => m.payerConfigRoutes
          ),
      },
      {
        // The page was renamed from "User Admin"; keep old links working.
        path: 'user-admin',
        pathMatch: 'full',
        redirectTo: 'global-admin',
      },
      {
        path: 'global-admin',
        title: 'Global Admin',
        canActivate: [AuthorizationRiGuard],
        data: {
          permissions: [Permissions.ResolutionUsersEdit],
        },
        loadChildren: () =>
          import('./routes/global-admin/global-admin.routes').then(
            (m) => m.globalAdminRoutes
          ),
      },
      {
        path: 'portals',
        title: 'Manage Payer Portals',
        canActivate: [AuthorizationRiGuard],
        data: {
          permissions: [Permissions.ResolutionAccess],
        },
        loadChildren: () =>
          import('./routes/portals/portals.routes').then(
            (m) => m.portalsRoutes
          ),
      },
      {
        path: 'rpa-report',
        title: 'RPA Report',
        canActivate: [AuthorizationRiGuard],
        data: {
          permissions: [Permissions.ResolutionAccess],
        },
        loadComponent: () =>
          import('./routes/toolbox/components/rpa-report').then(
            (c) => c.RpaReportComponent
          ),
      },
      {
        path: 'tasks',
        pathMatch: 'full',
        redirectTo: 'pending-tasks',
      },
      {
        path: '',
        component: MainLayoutComponent,
        canActivate: [AuthorizationRiGuard],
        data: {
          permissions: ['resolution:access'],
        },
        children: [
          {
            path: 'pending-tasks',
            pathMatch: 'full',
            title: 'Pending Tasks',
            canActivate: [AuthorizationRiGuard],
            data: {
              permissions: ['resolution:tasks:assign'],
            },
            loadComponent: () =>
              import('./routes/tasks-pending/pending-tasks.component').then(
                (c) => c.PendingTasksComponent
              ),
          },
          {
            path: 'completed-tasks',
            pathMatch: 'full',
            title: 'Completed Tasks',
            loadComponent: () =>
              import('./routes/tasks-completed/completed-tasks.component').then(
                (c) => c.CompletedTasksComponent
              ),
          },
          {
            path: 'logos-config',
            pathMatch: 'full',
            title: 'Logos',
            loadComponent: () =>
              import('./routes/logos-config/logos-config.component').then(
                (c) => c.LogosConfigComponent
              ),
          },
          {
            path: '',
            component: RoutesWithSidebarComponent,
            children: [
              {
                path: 'source-systems/:sourceSystemId/partitions/:partitionId/health-systems/:healthSystemId/accounts/:accountNumber',
                canDeactivate: [DirtyCheckGuard],
                loadComponent: () =>
                  import('./routes/account-view/account-view.component').then(
                    (c) => c.AccountViewComponent
                  ),
                resolve: {
                  collapseSidebar: () => {
                    // By default sidebar for this route should be collapsed.
                    // This call to setCollapsed is provided here because
                    // we need to set this state earlier before anything is
                    // rendered on the screen. If to place this call to
                    // component level then user will see animation of the
                    // sidebar collapse on initial page loading.
                    const sidebarState = inject(SidebarState);
                    sidebarState.updateState([collapsedSetter(true)]);

                    return true;
                  },
                  entityIds: (route: ActivatedRouteSnapshot) => {
                    const opportunityId =
                      (route.queryParams['opportunityId'] as
                        | string
                        | undefined) || '';
                    const entityIds: EntityIds = {
                      accountNumber: route.params['accountNumber'],
                      healthSystemId: route.params['healthSystemId'],
                      partitionId: route.params['partitionId'],
                      sourceSystemId: route.params['sourceSystemId'],
                      opportunityId,
                    };
                    return entityIds;
                  },
                },
              },
              {
                path: 'tasks',
                title: 'Task',
                loadChildren: () =>
                  import('./routes/tasks-worker/tasks-worker.routes').then(
                    (r) => r.tasksWorkerRoutes
                  ),
              },
              {
                path: 'task-groups',
                loadChildren: () =>
                  import('./routes/tasks-worker/tasks-worker.routes').then(
                    (r) => r.taskGroupsWorkerRoutes
                  ),
              },
              {
                path: 'access-request',
                component: AccessRequestComponent,
                title: 'Access Request',
              },
            ],
          },
        ],
      },
      {
        path: 'user-preferences',
        pathMatch: 'full',
        redirectTo: AFTER_TASK_COMPLETION_SEGMENTS.join('/'),
      },
      {
        path: '**',
        title: 'Page Not Found',
        loadComponent: () =>
          import('./shared/components/page-not-found/page-not-found.component').then(
            (m) => m.PageNotFoundComponent
          ),
      },
    ],
  },
];
